"use client"

import type { UseFormRegister, UseFormSetValue } from "react-hook-form"
import { format } from "date-fns"
import { Briefcase, CalendarIcon, User } from "lucide-react"

import { FIELD_MONO, FIELD_UNDERLINE, FieldShell, Segmented } from "@/components/graphite/field"
import { TimePicker } from "@/components/time-picker"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import type { CalendarEventFormDataV2 } from "@/types/calendar"
import type { GetEventsBusySlots200SlotsItem } from "openapi/api/model"

import { OverlapWarning } from "./calendar-event-shared"

/**
 * The event form's fields, in canvas 14e's language: underlines, not boxes.
 *
 * They had been `Label` over boxed `Input` at `text-sm md:text-base` — which is
 * the wrong axis twice over. `md:` asks the window, and this form lives inside
 * a dialog at most 672px wide, so the "responsive" sizing responded to
 * something that has nothing to do with the space the field is in.
 */

/** A select trigger wearing the field underline instead of its own box. */
const TRIGGER = cn(FIELD_UNDERLINE, "h-auto pb-[7px] text-[12.5px] leading-[1.4]")

/** The four the calendar's legend draws, and the set `event_type` takes. */
const EVENT_TYPES = ["interview", "screening", "meeting", "other"] as const

/** A field the form will not submit without. */
function Required({ children }: { children: string }) {
  return (
    <>
      {children} <span className="text-destructive">*</span>
    </>
  )
}

type EventBasicsSectionProps = {
  eventType: CalendarEventFormDataV2["event_type"]
  setValue: UseFormSetValue<CalendarEventFormDataV2>
  register: UseFormRegister<CalendarEventFormDataV2>
}

export function EventBasicsSection({ eventType, setValue, register }: EventBasicsSectionProps) {
  return (
    <>
      <FieldShell label={<Required>Event title</Required>}>
        <input
          id="title"
          {...register("title", { required: true })}
          placeholder="e.g., John Doe @Senior Developer"
          aria-label="Event title"
          className={cn(FIELD_UNDERLINE, "pb-[7px] text-[13px] leading-[1.4]")}
        />
      </FieldShell>

      {/* Four words, one of which is true. A dropdown would hide three of them
          behind a click for nothing — 15a's segmented control is for this. */}
      <Segmented
        label="Event type"
        options={EVENT_TYPES}
        value={eventType}
        onChange={(value) => setValue("event_type", value)}
        className="mt-5"
      />
    </>
  )
}

type EventLinkingSectionProps = {
  linkType: "job" | "candidate"
  selectedJob: string
  selectedCandidate: string
  jobs?: Array<{ id: string; title?: string | null; company_name?: string | null }>
  candidates?: Array<{ id: string; full_name: string; stage?: string }>
  setValue: UseFormSetValue<CalendarEventFormDataV2>
  isPresetCandidate?: boolean
  isPresetJob?: boolean
}

export function EventLinkingSection({
  linkType,
  selectedJob,
  selectedCandidate,
  jobs,
  candidates,
  setValue,
  isPresetCandidate = false,
  isPresetJob = false,
}: EventLinkingSectionProps) {
  return (
    /* A hairline and space, not a tinted box: a form is one column of fields,
       and a panel inside it reads as a second form. */
    <div className="mt-6 border-t pt-5">
      {!isPresetCandidate && (
        <FieldShell label="Link type">
          <Select
            value={linkType}
            onValueChange={(value: "job" | "candidate") => {
              setValue("linkType", value)
              setValue("candidate_id", "")
            }}
          >
            <SelectTrigger aria-label="Link type" className={TRIGGER}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="job">
                <span className="flex items-center gap-2">
                  <Briefcase className="size-3.5" />
                  Job
                </span>
              </SelectItem>
              <SelectItem value="candidate">
                <span className="flex items-center gap-2">
                  <User className="size-3.5" />
                  Candidate
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        </FieldShell>
      )}

      <FieldShell label={<Required>Job</Required>} className="mt-5">
        <Select
          value={selectedJob}
          disabled={isPresetJob}
          onValueChange={(value) => {
            setValue("job_id", value)
            if (!isPresetCandidate) {
              setValue("candidate_id", "")
            }
          }}
        >
          <SelectTrigger aria-label="Job" className={TRIGGER}>
            <SelectValue placeholder="Choose a job..." />
          </SelectTrigger>
          <SelectContent>
            {jobs?.map((job) => (
              <SelectItem key={job.id} value={job.id}>
                <span className="block truncate">
                  {job.title} - {job.company_name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldShell>

      {!isPresetCandidate && linkType === "candidate" && selectedJob && (
        <FieldShell label={<Required>Candidate</Required>} className="mt-5">
          <Select value={selectedCandidate} onValueChange={(value) => setValue("candidate_id", value)}>
            <SelectTrigger aria-label="Candidate" className={TRIGGER}>
              <SelectValue placeholder="Choose a candidate..." />
            </SelectTrigger>
            <SelectContent>
              {candidates?.map((candidate) => (
                <SelectItem key={candidate.id} value={candidate.id}>
                  <span className="block truncate">
                    {candidate.full_name}
                    {candidate.stage ? ` (${candidate.stage})` : ""}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>
      )}
    </div>
  )
}

type EventScheduleSectionProps = {
  datePickerOpen: boolean
  setDatePickerOpen: (open: boolean) => void
  selectedDate?: Date
  startTime: string
  endTime: string
  onDateSelect: (date: Date) => void
  onStartTimeChange: (value: string) => void
  onEndTimeChange: (value: string) => void
  overlappingBusySlots: GetEventsBusySlots200SlotsItem[]
}

export function EventScheduleSection({
  datePickerOpen,
  setDatePickerOpen,
  selectedDate,
  startTime,
  endTime,
  onDateSelect,
  onStartTimeChange,
  onEndTimeChange,
  overlappingBusySlots,
}: EventScheduleSectionProps) {
  return (
    <>
      <FieldShell label="Date" className="mt-6 border-t pt-5">
        <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={selectedDate ? `Date, ${format(selectedDate, "EEEE d MMMM yyyy")}` : "Date"}
              className={cn(
                FIELD_UNDERLINE,
                FIELD_MONO,
                "flex items-center gap-2 pb-[7px] text-left",
                !selectedDate && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="size-3.5 flex-none" />
              {selectedDate ? format(selectedDate, "EEE d MMM yyyy") : "—"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                onDateSelect(date || new Date())
                setDatePickerOpen(false)
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </FieldShell>

      {/* `@sm`, not `sm:`: the two clocks share a line when the dialog has room
          for them, which is not the same question as how wide the window is. */}
      <div className="mt-5 grid grid-cols-1 gap-5 @sm:grid-cols-2">
        <FieldShell label="Start">
          <TimePicker name="Start" value={startTime} onChange={onStartTimeChange} />
        </FieldShell>
        <FieldShell label="End">
          <TimePicker name="End" value={endTime} onChange={onEndTimeChange} />
        </FieldShell>
      </div>

      <OverlapWarning slots={overlappingBusySlots} />
    </>
  )
}

type EventLocationNotesSectionProps = {
  register: UseFormRegister<CalendarEventFormDataV2>
}

export function EventLocationNotesSection({ register }: EventLocationNotesSectionProps) {
  return (
    <>
      <FieldShell label="Location" className="mt-6 border-t pt-5">
        <input
          id="location"
          {...register("location")}
          placeholder="e.g., San Francisco, CA or Remote"
          aria-label="Location"
          className={cn(FIELD_UNDERLINE, "pb-[7px] text-[12.5px] leading-[1.4]")}
        />
      </FieldShell>

      <FieldShell label="Notes" className="mt-5">
        <textarea
          id="notes"
          {...register("notes")}
          placeholder="Add any additional notes..."
          rows={3}
          aria-label="Notes"
          className={cn(FIELD_UNDERLINE, "resize-none pb-2.5 text-[12.5px] leading-[1.7]")}
        />
      </FieldShell>
    </>
  )
}
